/* eslint-disable react/no-array-index-key */
import {Box, IconButton, Text} from '@chakra-ui/react'
import {useCan} from '@/shared/hooks/use-permissions'
import {useTranslate} from '@tolgee/react'
import {LuTrash2} from 'react-icons/lu'
import {Table, Badge} from '@chakra-ui/react'
import {Patient, PatientGfr} from '@cypro/engine/schema'
import {useGetPatientLatestGfrLab} from '@/modules/patients/api/use-get-patient-latest-gfr-lab'
import {formatGfr} from '@/modules/patients/functions'
import {useConfirmDeletePatientGfr} from '@/modules/patients/api/use-delete-patient-gfr'
import {openGfrDrawer} from './gfr-drawer-util'
import {formatDateKey, getDatetimeFormatter, multipleDigitsNumberFormatter} from '@cypro/ui/utils'

interface Props {
  gfrLabs: PatientGfr[]
  patient: Patient
}

export function GfrTable({gfrLabs, patient}: Props) {
  const {t} = useTranslate('common')
  const canEditPatient = useCan('patients.edit')
  const [inUseGfr] = useGetPatientLatestGfrLab(patient.id)
  const confirmDeleteGfr = useConfirmDeletePatientGfr(patient)
  return (
    <Table.Root>
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeader>{t('gfr_table.sample_date')}</Table.ColumnHeader>
          <Table.ColumnHeader>{t('gfr_table.calculation_method')}</Table.ColumnHeader>
          <Table.ColumnHeader>{t('gfr_table.serum')}</Table.ColumnHeader>
          <Table.ColumnHeader>{t('gfr_table.weight')}</Table.ColumnHeader>
          <Table.ColumnHeader>{t('gfr_table.height')}</Table.ColumnHeader>
          <Table.ColumnHeader>{t('gfr_table.entered_on')}</Table.ColumnHeader>
          <Table.ColumnHeader>{t('gfr_table.gfr')}</Table.ColumnHeader>
          <Table.ColumnHeader>{t('gfr_table.in_use')}</Table.ColumnHeader>
          <Table.ColumnHeader />
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {gfrLabs.map(gfr => {
          return (
            <Table.Row
              key={gfr.id}
              role="button"
              tabIndex={0}
              cursor="pointer"
              _hover={{bg: 'gray.50'}}
              _focusVisible={{outline: '2px solid', outlineColor: 'blue.500'}}
              onClick={() => openGfrDrawer(gfr, patient)}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  openGfrDrawer(gfr, patient)
                }
              }}
            >
              <Table.Cell border="none">{formatDateKey(gfr.labTime)}</Table.Cell>
              <Table.Cell border="none">
                <Badge size="xs">{t(`gfr.${gfr.method}`)}</Badge>
              </Table.Cell>
              <Table.Cell border="none">
                <Box display={'flex'} flexDirection={'column'} gap={'4px'}>
                  <Text fontWeight={'normal'} fontSize={'sm'} color={'fg.muted'}>
                    {gfr.serumCreatinine ? `${gfr.serumCreatinine} ${t(`serum_unit.${gfr.serumUnit}`)}` : '-'}
                  </Text>
                </Box>
              </Table.Cell>
              <Table.Cell border="none">
                <Text fontWeight="normal" fontSize="sm" color="fg.muted">
                  {gfr.weight ? multipleDigitsNumberFormatter.format(gfr.weight) : '-'}
                </Text>
              </Table.Cell>
              <Table.Cell border="none">
                <Text fontWeight="normal" fontSize="sm" color="fg.muted">
                  {gfr.height ? multipleDigitsNumberFormatter.format(gfr.height) : '-'}
                </Text>
              </Table.Cell>
              <Table.Cell border="none">
                <Text fontWeight="normal" fontSize="sm" color="fg.muted">
                  {gfr.createdAt ? getDatetimeFormatter().format(gfr.createdAt) : '-'}
                </Text>
              </Table.Cell>
              <Table.Cell border="none">
                <Box display={'flex'} flexDirection={'column'} gap={'4px'}>
                  <Text fontWeight="normal" fontSize="sm" color="fg.muted">
                    {formatGfr(gfr.gfr)}
                  </Text>
                </Box>
              </Table.Cell>
              <Table.Cell border="none">
                {gfr.id === inUseGfr?.id && (
                  <Badge size="xs" colorPalette="green">
                    {t('gfr.in_use')}
                  </Badge>
                )}
              </Table.Cell>
              <Table.Cell border="none" textAlign="end">
                {canEditPatient && (
                  <IconButton
                    aria-label={t('gfr.delete')}
                    variant="ghost"
                    colorPalette="red"
                    size="xs"
                    onClick={event => {
                      event.stopPropagation()
                      void confirmDeleteGfr(gfr)
                    }}
                    onKeyDown={event => {
                      event.stopPropagation()
                    }}
                  >
                    <LuTrash2 />
                  </IconButton>
                )}
              </Table.Cell>
            </Table.Row>
          )
        })}
      </Table.Body>
    </Table.Root>
  )
}
